import { useMemo } from "react";

import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material";

import {
  ErrorCode,
  FileError,
  FileRejection,
  useDropzone,
  Accept,
} from "react-dropzone";
import { useFormContext } from "react-hook-form";

import { bytesToSize } from "@covertree/utils";
import { colors } from "@covertree/theme/colors";

interface Props
  extends Omit<
    React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    >,
    "name" | "accept"
  > {
  name: string;
  accept?: Accept;
  maxFileSize: number;
  maxFileAmount?: number;
  label?: string;
  hidden?: boolean;
  validator?: (
    file: File,
    currentFiles: File[]
  ) => FileError | FileError[] | null;
}

function FormFileField(props: Props) {
  const {
    name,
    label,
    accept,
    disabled,
    maxFileSize,
    maxFileAmount,
    validator,
    hidden = false,
  } = props;
  const theme = useTheme();
  const { register, setValue, watch } = useFormContext();

  const files: File[] = watch(name) || [];

  const errorMessageMap = useMemo<Record<ErrorCode, string>>(
    () => ({
      [ErrorCode.FileInvalidType]:
        "The file type of the attachment(s) is incorrect",
      [ErrorCode.FileTooLarge]: `The size of attachment cannot be larger than ${bytesToSize(
        maxFileSize
      )}`,
      [ErrorCode.FileTooSmall]: "",
      [ErrorCode.TooManyFiles]: `The total amount of files uploaded cannot be larger than ${maxFileAmount}.`,
    }),
    [maxFileAmount, maxFileSize]
  );

  const isAnyFileSizeOverLimit = () => {
    return !!files.filter((file: File) => file.size > maxFileSize).length;
  };

  const onDrop = (
    droppedFilesWithDuplicates: File[],
    rejectedFiles: FileRejection[]
  ) => {
    const droppedFiles = droppedFilesWithDuplicates.reduce(
      (acc: File[], curr: File) => {
        if (![...files, ...acc].find((file) => file.name === curr.name)) {
          acc.push(curr);
        }
        return acc;
      },
      []
    );
    const allFiles = [...droppedFiles, ...files];
    const filesWithSizeOverLimit = droppedFiles.filter(
      (file) => file.size > maxFileSize
    );

    if (!!filesWithSizeOverLimit.length) {
      filesWithSizeOverLimit.forEach((file) => {
        rejectedFiles.push({
          file,
          errors: [
            {
              code: ErrorCode.FileTooLarge,
              message: errorMessageMap[ErrorCode.FileTooLarge],
            },
          ],
        });
      });
      return;
    }

    if (maxFileAmount && allFiles.length > maxFileAmount) {
      droppedFiles.forEach((file) => {
        rejectedFiles.push({
          file,
          errors: [
            {
              code: ErrorCode.TooManyFiles,
              message: errorMessageMap[ErrorCode.TooManyFiles],
            },
          ],
        });
      });
      return;
    }

    setValue(name, [...files, ...droppedFiles], {
      shouldValidate: true,
    });
  };

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    accept,
    disabled: disabled || isAnyFileSizeOverLimit(),
    validator: (file) => {
      if (!validator) {
        return null;
      }

      return validator(file, files);
    },
  });

  return (
    <Stack sx={hidden ? { display: "none" } : []}>
      <InputLabel htmlFor={name}>{label}</InputLabel>
      {fileRejections?.length > 0 && (
        <Box mb={2}>
          <FormHelperText
            error
            {...(!fileRejections[0]?.errors[0].message && {
              sx: { margin: 0 },
            })}>
            {errorMessageMap[fileRejections[0]?.errors[0].code as ErrorCode] ??
              fileRejections[0]?.errors[0].message}
          </FormHelperText>
        </Box>
      )}
      <Box id={name} role="button" {...getRootProps()}>
        <input
          {...register(name)}
          {...props}
          accept={accept ? Object.keys(accept).join(", ") : ""}
          {...getInputProps()}
          data-testid="file-drop-input"
        />
        <Box
          sx={{
            border: "dashed 2px",
            borderColor: colors.grey3,
            borderRadius: theme.spacing(2),
            cursor: "pointer",
            padding: theme.spacing(6, 2),
          }}>
          <Typography
            variant="body2"
            align="center"
            fontSize={18}
            color={theme.palette.text.disabled}>
            Drop your files or click here to upload
          </Typography>
          <br />
          <Typography
            variant="body2"
            align="center"
            fontSize={16}
            color={theme.palette.text.secondary}>
            {`The size of single attachment is limited to a maximum of ${bytesToSize(
              maxFileSize
            )}. The allowed file types are ${Object.values(accept || {}).join(
              ", "
            )}`}
          </Typography>
        </Box>
      </Box>
    </Stack>
  );
}

export default FormFileField;
